import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import { useAtomValue } from "jotai";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked, usePageTrackingControls } from "~/contexts/page-trackings";
import { useUserTokenAlgolia } from "~/hooks/use-user-token-algolia";
import { convertProductFromAgoliaToEva, getDiscountedAndOOSProductsByIds } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Container from "../common/container";
import Grid from "../common/grid";
import LinkWrapper from "../common/link-wrapper";
import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import EditorialEntry from "../editorials/editorial-entry";
import CustomSearchBox from "../search/custom-search-box";
import NoResults from "../search/no-results";
import AlgoliaBanner from "./algolia-banner";
import CategoryFilters from "./category-filters";
import ProductTileAlgolia from "./product-tile-algolia";
import styles from "./search-product-grid.module.scss";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  noResultsComponents?: Entry<unknown>[];
  initialResultsHits: any[];
  algoliaUserToken: string;
};

export default function SearchProductGridAlgolia(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const [path, setPath] = useState<string | undefined>(undefined);
  const { results, status } = useInstantSearch();
  const { configuration, algoliaIndexId, eva } = useConfiguration();
  const searchParams = useSearchParams();
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);

  useUserTokenAlgolia({ token: props.algoliaUserToken });
  const { items: hits, isLastPage, showMore, sendEvent, isFirstPage, showPrevious, banner } = useInfiniteHits();

  const lastTrackedIndex = useRef<number>(0);
  const isPageTracked = usePageTracked();
  const trackingControls = usePageTrackingControls();

  const searchKey = `${algoliaIndexId}[query]`;
  const actualQuery = searchParams.get(searchKey);

  const { data: discountedAndOOSProducts } = useQuery<Algolia.DiscountedAndOOSProducts>({
    queryKey: ["algoliaSearchAvailabilityAndDiscounts", hits.map((hit) => hit.code)],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: async () => {
      return (
        (await getDiscountedAndOOSProductsByIds(
          eva,
          hits.map((hit) => hit.objectID)
        )) ?? {}
      );
    },
    enabled: hits.length > 0,
  });

  useEffect(() => {
    const newPath = window.location.href;

    setPath(newPath);
    if (path && path !== newPath) {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  useEffect(() => {
    if (isPageTracked) {
      if (hits) {
        lastTrackedIndex.current = TrackingUtils.trackAlgoliaProductsListImpression(
          hits as Algolia.Product[],
          lastTrackedIndex.current,
          "algolia_search",
          {
            currency: configuration.CurrencyID,
          }
        );
      }
    } else {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, hits, lastTrackedIndex.current]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const sentinelRef2 = useRef<HTMLDivElement>(null);
  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });
  const sentinel2InView = useInView(sentinelRef2, { margin: "0% 0% -50% 0%" });
  const stickySentinelInView = useInView(sentinelRef, { margin: "0px 0px -135px 0px" });

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);

    if (usableNetContainer && breakpoint == "mobile" && sentinelRef.current) {
      if (!stickySentinelInView && sentinelRef.current.getBoundingClientRect().top > 0) {
        usableNetContainer.style.bottom = "65px";
      } else {
        usableNetContainer.style.bottom = "";
      }
    }

    return () => {
      if (usableNetContainer) {
        usableNetContainer.style.bottom = "";
      }
    };
  }, [sequentialLoadingReady.usablenet, breakpoint, stickySentinelInView]);

  // handle infinite scroll - show previous page
  useEffect(() => {
    if (sentinel2InView && !isFirstPage && status === "idle") {
      showPrevious();
    }
  }, [isFirstPage, sentinel2InView, showPrevious, status]);

  // handle infinite scroll
  useEffect(() => {
    if (sentinelInView && !isLastPage && status === "idle") {
      showMore();
    }
  }, [isLastPage, sentinelInView, showMore, status]);

  const clickEvent = async (algoliaProduct: Algolia.Product) => {
    sendEvent?.("click", algoliaProduct, "Hit Clicked");
    if ("__queryID" in algoliaProduct) {
      // save it in localstorage for cross-tabs navigation and set it as default on session
      localStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
      sessionStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
    }
  };

  return (
    <>
      <Head>
        <title>{`${t("generic.searched")} "${actualQuery || ""}" | ${t("generic.pageTitleSuffix")}`}</title>
        <meta name="robots" content="none" />
      </Head>
      <div className={styles.searchedBar}>
        <p>{t("generic.searched")}</p>
        <CustomSearchBox
          onChange={() => {
            trackingControls.forceStatus(false);
          }}
        />
      </div>
      <Container className={styles.reducedMobileContainer}>
        {results.nbHits > 0 ? (
          <div className={styles.prodCount}>
            <p>{t("generic.n_products", { number: results.nbHits })}</p>
          </div>
        ) : banner ? (
          <div className={styles.wrapperBanner}>
            <AlgoliaBanner banner={banner} />
          </div>
        ) : (
          <div className={styles.noResults}>
            <h2>{t("generic.noresults")}</h2>
            <p className={styles.noResultsDescription}>{t("algolia.noResultsDescription")}</p>
            <div className={styles.wrapperLinks}>
              <LinkWrapper lookAsButton="secondary" href={appRoutes.TRACK_YOUR_ORDER}>
                {t("generic.track")}
              </LinkWrapper>
              <LinkWrapper lookAsButton="secondary" href={appRoutes.CUSTOMER_HELP_CENTER}>
                {t("generic.helpCenter")}
              </LinkWrapper>
            </div>
          </div>
        )}
        <NoResults />
        <Grid className={styles.grid}>
          <div className={styles.sentinelTop} ref={sentinelRef2} aria-hidden="true" />
          {hits.length === 0 && status !== "idle"
            ? props.initialResultsHits.map((product: any) => (
                <div key={product.objectID + "-initial"} className={styles.tile}>
                  <ProductTileAlgolia product={product} listScope="search" />
                </div>
              ))
            : (hits as Algolia.Product[]).map((item, idx) => (
                <div key={item.objectID} className={styles.tile}>
                  <ProductTile
                    product={convertProductFromAgoliaToEva(item, configuration.CurrencyID)}
                    idxInList={idx}
                    listScope="search"
                    noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
                    algolia={{ product: item, discountedAndOOSProducts, events: { clickEvent } }}
                  />
                </div>
              ))}
          <div className={styles.sentinelBottom} ref={sentinelRef} aria-hidden="true" />
        </Grid>
        {results.nbHits > 0 && (
          <div className={styles.filters}>
            <CategoryFilters pageType="inSearch" />
          </div>
        )}
      </Container>
      {/* show editorial entries from search page contentful */}
      {!results.nbHits &&
        props.noResultsComponents
          ?.filter(isActiveEntry)
          .map((entry) => <EditorialEntry key={entry.sys.id} entry={entry} />)}
    </>
  );
}
